import { useState, useEffect } from "react";
import { TokenChainSelectionModal } from "./TokenChainSelectionModal";
import { api } from "../lib/api-client";
import { getTokenLogoUrl, getTokenColor } from "../lib/tokenLogos";

interface RampCardProps {
  mode: "ONRAMP" | "OFFRAMP";
  formData: {
    amount: string;
    cryptoAmount: string;
    cryptoCurrency: string;
    cryptoNetwork: string;
    currency: string;
    countryCode: string;
    walletAddress: string;
    walletTag: string;
  };
  onFormDataChange: (data: any) => void;
  cryptoChannels: any;
  availableCurrencies: Array<{
    code: string;
    name: string;
    defaultNetwork: string;
    supportedNetworks: string[];
  }>;
  availableNetworks: Array<{
    code: string;
    name: string;
    requiresMemo: boolean;
  }>;
  availableFiatCurrencies: Array<{
    code: string;
    name: string;
    symbol: string;
  }>;
  onModeSwitch: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  error: string;
  loadingChannels: boolean;
  isInTransaction?: boolean;
}

export function RampCard({
  mode,
  formData,
  onFormDataChange,
  cryptoChannels,
  availableCurrencies,
  availableNetworks,
  availableFiatCurrencies,
  onModeSwitch,
  onSubmit,
  isLoading,
  error,
  loadingChannels,
  isInTransaction = false,
}: RampCardProps) {
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [walletError, setWalletError] = useState("");
  const [usdEquivalent, setUsdEquivalent] = useState<string>("0.00");
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [estimatedCrypto, setEstimatedCrypto] = useState<string>("0.00");
  const [cryptoPrice, setCryptoPrice] = useState<number | null>(null);
  const [fetchingRate, setFetchingRate] = useState(false);

  // Format number with commas
  const formatNumberWithCommas = (value: string): string => {
    if (!value) return "";
    const parts = value.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  // Remove commas from number
  const removeCommas = (value: string): string => {
    return value.replace(/,/g, "");
  };

  // Fetch real-time exchange rate and estimated amounts (with debouncing)
  useEffect(() => {
    // Reset values if no valid amount
    if (
      (mode === "ONRAMP" && (!formData.amount || parseFloat(formData.amount) <= 0)) ||
      (mode === "OFFRAMP" && (!formData.cryptoAmount || parseFloat(formData.cryptoAmount) <= 0))
    ) {
      setUsdEquivalent("0.00");
      setExchangeRate(null);
      setEstimatedCrypto("0.00");
      setCryptoPrice(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setFetchingRate(true);

        if (mode === "ONRAMP") {
          // For onramp: get fiat→USD rate and crypto price
          const [rateResponse, priceResponse] = await Promise.all([
            api.crypto.rate({
              amount: formData.amount,
              fromCurrency: formData.currency,
              toCurrency: "USD",
              type: "BUY",
              countryCode: formData.countryCode,
            }),
            fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${getCoinGeckoId(formData.cryptoCurrency)}&vs_currencies=usd`)
              .then(res => res.json())
              .catch(() => null),
          ]);

          const usdAmount = rateResponse.data?.toAmount || 0;
          const rate = rateResponse.data?.rate || 0;
          setUsdEquivalent(usdAmount.toFixed(2));
          setExchangeRate(rate);

          // Get crypto price and calculate estimated crypto amount
          const coinId = getCoinGeckoId(formData.cryptoCurrency);
          const price = priceResponse?.[coinId]?.usd || 0;
          setCryptoPrice(price);

          if (price > 0) {
            const estimatedCryptoAmount = usdAmount / price;
            setEstimatedCrypto(estimatedCryptoAmount.toFixed(6));
          }
        } else {
          // For offramp: get crypto price and calculate fiat amount
          const priceResponse = await fetch(
            `https://api.coingecko.com/api/v3/simple/price?ids=${getCoinGeckoId(formData.cryptoCurrency)}&vs_currencies=usd`
          ).then(res => res.json()).catch(() => null);

          const coinId = getCoinGeckoId(formData.cryptoCurrency);
          const price = priceResponse?.[coinId]?.usd || 0;
          setCryptoPrice(price);

          if (price > 0) {
            const cryptoAmt = parseFloat(formData.cryptoAmount);
            const usdValue = cryptoAmt * price;
            setUsdEquivalent(usdValue.toFixed(2));

            // Get local currency rate
            const rateResponse = await api.crypto.rate({
              amount: "100",
              fromCurrency: formData.currency,
              toCurrency: "USD",
              type: "SELL",
              countryCode: formData.countryCode,
            });

            const rate = rateResponse.data?.rate || 0;
            setExchangeRate(rate);

            if (rate > 0) {
              const localCurrencyAmount = usdValue * rate;
              setEstimatedCrypto(localCurrencyAmount.toFixed(2));
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch rate:", error);
      } finally {
        setFetchingRate(false);
      }
    }, 500); // Debounce: wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [
    formData.amount,
    formData.cryptoAmount,
    formData.currency,
    formData.countryCode,
    formData.cryptoCurrency,
    mode,
  ]);

  // Helper to get CoinGecko ID from token symbol
  const getCoinGeckoId = (symbol: string): string => {
    const mapping: Record<string, string> = {
      BTC: 'bitcoin',
      ETH: 'ethereum',
      USDT: 'tether',
      USDC: 'usd-coin',
      SOL: 'solana',
      MATIC: 'matic-network',
      POL: 'matic-network',
      BNB: 'binancecoin',
      AVAX: 'avalanche-2',
      DAI: 'dai',
      BUSD: 'binance-usd',
      XRP: 'ripple',
      ADA: 'cardano',
      XLM: 'stellar',
      CUSD: 'celo-dollar',
      XAUT: 'tether-gold',
      PYUSD: 'paypal-usd',
    };
    return mapping[symbol.toUpperCase()] || symbol.toLowerCase();
  };

  const handleTokenChainSelect = (token: string, chain: string) => {
    onFormDataChange({
      ...formData,
      cryptoCurrency: token,
      cryptoNetwork: chain,
    });
  };

  // Token icon component with logo support
  const TokenIcon = ({
    symbol,
    size = "w-8 h-8",
  }: {
    symbol: string;
    size?: string;
  }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const logoUrl = getTokenLogoUrl(symbol);
    const hasLogo = !!logoUrl;

    // Reset states when symbol changes
    useEffect(() => {
      setImageLoaded(false);
      setImageError(false);
    }, [symbol, logoUrl]);

    return (
      <div
        className={`${size} rounded-full flex items-center justify-center font-bold text-xs overflow-hidden transition-all duration-200`}
        style={{
          background: (!hasLogo || !imageLoaded || imageError) ? getTokenColor(symbol) : "#FFFFFF",
          padding: (hasLogo && imageLoaded && !imageError) ? "4px" : "0",
        }}
      >
        {hasLogo && !imageError ? (
          <img
            src={logoUrl}
            alt={symbol}
            className={`w-full h-full object-contain transition-opacity duration-200 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        ) : null}
        {(!hasLogo || !imageLoaded || imageError) && (
          <span className="text-white">{symbol.substring(0, 3)}</span>
        )}
      </div>
    );
  };

  const getNetworkName = (): string => {
    const network = availableNetworks.find(
      (n) => n.code === formData.cryptoNetwork,
    );
    return network?.name || formData.cryptoNetwork;
  };

  const validateWalletAddress = (address: string): boolean => {
    if (!address || address.trim() === "") {
      setWalletError("Wallet address is required");
      return false;
    }

    const patterns: Record<string, RegExp> = {
      TRC20: /^T[A-Za-z1-9]{33}$/,
      ERC20: /^0x[a-fA-F0-9]{40}$/,
      BEP20: /^0x[a-fA-F0-9]{40}$/,
      SOL: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
      MATIC: /^0x[a-fA-F0-9]{40}$/,
      BTC: /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/,
      XLM: /^G[A-Z2-7]{55}$/,
    };

    const pattern = patterns[formData.cryptoNetwork];
    if (pattern && !pattern.test(address.trim())) {
      setWalletError(`Invalid ${formData.cryptoNetwork} address format`);
      return false;
    }

    setWalletError("");
    return true;
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate wallet address for onramp
    if (mode === "ONRAMP" && !validateWalletAddress(formData.walletAddress)) {
      return;
    }

    // Validate memo for networks that require it
    const network = availableNetworks.find(
      (n) => n.code === formData.cryptoNetwork,
    );
    if (mode === "ONRAMP" && network?.requiresMemo && !formData.walletTag) {
      setWalletError("Memo/Tag is required for this network");
      return;
    }

    onSubmit(e);
  };

  const getCurrencySymbol = (code: string): string => {
    const currency = availableFiatCurrencies.find((c) => c.code === code);
    return currency?.symbol || code;
  };

  return (
    <>
      <div className="max-w-xl mx-auto">
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {/* Jumper-style Header */}
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl md:text-2xl font-bold text-white">
              {mode === "ONRAMP" ? "Buy Crypto" : "Sell Crypto"}
            </h2>
          </div>

          {/* Main Card Container */}
          <div className="swap-card rounded-2xl p-4 md:p-6 space-y-4">
            {/* FROM / TO Section - Jumper Style */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-3">
                {/* FROM Card */}
                <button
                  type="button"
                  onClick={() => {
                    if (mode === "ONRAMP") {
                      // For onramp: FROM is fiat (could add fiat selection later)
                    } else {
                      // For offramp: FROM is crypto
                      setShowTokenModal(true);
                    }
                  }}
                  className="swap-card rounded-xl p-4 hover:bg-slate-800/40 transition-colors text-left"
                >
                  <div className="text-xs text-slate-400 mb-2 uppercase">
                    From
                  </div>
                  <div className="flex items-center gap-2">
                    {mode === "ONRAMP" ? (
                      <>
                        {/* Fiat Currency */}
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-xs">
                          {getCurrencySymbol(formData.currency)}
                        </div>
                        <div>
                          <div className="font-semibold text-white">
                            {formData.currency}
                          </div>
                          <div className="text-xs text-slate-400">
                            Nigerian Naira
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Crypto */}
                        <TokenIcon symbol={formData.cryptoCurrency} />
                        <div className="flex-1">
                          <div className="font-semibold text-white">
                            {formData.cryptoCurrency}
                          </div>
                          <div className="text-xs text-slate-400">
                            {getNetworkName()}
                          </div>
                        </div>
                        <svg
                          className="w-4 h-4 text-slate-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </>
                    )}
                  </div>
                </button>

                {/* TO Card */}
                <button
                  type="button"
                  onClick={() => {
                    if (mode === "ONRAMP") {
                      // For onramp: TO is crypto
                      setShowTokenModal(true);
                    } else {
                      // For offramp: TO is fiat
                    }
                  }}
                  className="swap-card rounded-xl p-4 hover:bg-slate-800/40 transition-colors text-left"
                >
                  <div className="text-xs text-slate-400 mb-2 uppercase">
                    To
                  </div>
                  <div className="flex items-center gap-2">
                    {mode === "ONRAMP" ? (
                      <>
                        {/* Crypto */}
                        <TokenIcon symbol={formData.cryptoCurrency} />
                        <div className="flex-1">
                          <div className="font-semibold text-white">
                            {formData.cryptoCurrency}
                          </div>
                          <div className="text-xs text-slate-400">
                            {getNetworkName()}
                          </div>
                        </div>
                        <svg
                          className="w-4 h-4 text-slate-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </>
                    ) : (
                      <>
                        {/* Fiat Currency */}
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-xs">
                          {getCurrencySymbol(formData.currency)}
                        </div>
                        <div>
                          <div className="font-semibold text-white">
                            {formData.currency}
                          </div>
                          <div className="text-xs text-slate-400">
                            Nigerian Naira
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </button>
              </div>

              {/* Swap Toggle Button in the middle */}
              {!isInTransaction && (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <button
                    type="button"
                    onClick={onModeSwitch}
                    className="w-10 h-10 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center hover:bg-slate-700 hover:border-slate-600 transition-all hover:scale-110 active:scale-95"
                    aria-label={`Switch to ${mode === "ONRAMP" ? "Sell" : "Buy"} mode`}
                  >
                    <svg
                      className="w-5 h-5 text-slate-400 hover:text-white transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                      />
                    </svg>
                  </button>
                </div>
              )}
              {isInTransaction && (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="w-10 h-10 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center opacity-50 cursor-not-allowed">
                    <svg
                      className="w-5 h-5 text-slate-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </div>

            {/* Amount Section - Jumper Style */}
            <div className="space-y-2">
              <label className="text-xs text-slate-400 uppercase">Send</label>
              <div className="swap-card rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={
                      mode === "ONRAMP"
                        ? formatNumberWithCommas(formData.amount)
                        : formatNumberWithCommas(formData.cryptoAmount)
                    }
                    onChange={(e) => {
                      const valueWithCommas = e.target.value;
                      const valueWithoutCommas = removeCommas(valueWithCommas);
                      const value = valueWithoutCommas.replace(/[^0-9.]/g, "");
                      if (mode === "ONRAMP") {
                        onFormDataChange({ ...formData, amount: value });
                      } else {
                        onFormDataChange({ ...formData, cryptoAmount: value });
                      }
                    }}
                    className="bg-transparent border-none outline-none text-3xl md:text-4xl font-bold text-white w-full"
                    placeholder="0"
                    required
                  />
                  <div className="flex items-center gap-2 ml-4">
                    {mode === "ONRAMP" ? (
                      <>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs bg-gradient-to-br from-green-400 to-green-600">
                          {getCurrencySymbol(formData.currency)}
                        </div>
                        <span className="font-semibold text-white text-lg">
                          {formData.currency}
                        </span>
                      </>
                    ) : (
                      <>
                        <TokenIcon symbol={formData.cryptoCurrency} />
                        <span className="font-semibold text-white text-lg">
                          {formData.cryptoCurrency}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Exchange Rate Display */}
                {((mode === "ONRAMP" && formData.amount && parseFloat(formData.amount) > 0) ||
                  (mode === "OFFRAMP" && formData.cryptoAmount && parseFloat(formData.cryptoAmount) > 0)) && (
                  <div className="mt-4 bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                    <div className="space-y-2">
                      {/* You'll receive */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-400">You'll receive</span>
                        <span className="text-lg font-semibold text-white">
                          {fetchingRate ? (
                            <span className="text-sm">Calculating...</span>
                          ) : mode === "ONRAMP" ? (
                            <>≈ {estimatedCrypto} {formData.cryptoCurrency}</>
                          ) : (
                            <>≈ {formatNumberWithCommas(estimatedCrypto)} {formData.currency}</>
                          )}
                        </span>
                      </div>

                      {/* Exchange rate */}
                      {exchangeRate && !fetchingRate && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500">Exchange rate</span>
                          <span className="text-slate-400">
                            1 USD = {exchangeRate.toFixed(2)} {formData.currency}
                          </span>
                        </div>
                      )}

                      {/* Crypto price */}
                      {cryptoPrice && !fetchingRate && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500">Crypto price</span>
                          <span className="text-slate-400">
                            1 {formData.cryptoCurrency} = ${cryptoPrice.toFixed(2)} USD
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Min/Max Limits */}
                <div className="mt-3 text-xs text-slate-500 text-right">
                  Min: 1,000{" "}
                  {mode === "ONRAMP"
                    ? formData.currency
                    : formData.cryptoCurrency}
                  {" • "}
                  Max: 5,000,000{" "}
                  {mode === "ONRAMP"
                    ? formData.currency
                    : formData.cryptoCurrency}
                </div>
              </div>
            </div>

            {/* Wallet Address Section - Only for Onramp */}
            {mode === "ONRAMP" && (
              <div className="space-y-2">
                <label className="text-xs text-slate-400 uppercase">
                  Your Wallet Address ({formData.cryptoNetwork}) *
                </label>
                <input
                  type="text"
                  value={formData.walletAddress}
                  onChange={(e) => {
                    onFormDataChange({
                      ...formData,
                      walletAddress: e.target.value,
                    });
                    setWalletError("");
                  }}
                  onBlur={() => {
                    if (formData.walletAddress) {
                      validateWalletAddress(formData.walletAddress);
                    }
                  }}
                  className="w-full bg-slate-900/60 border border-slate-700 rounded-xl px-4 py-3 text-white font-mono text-sm focus:ring-2 focus:ring-purple-500/50 focus:border-transparent outline-none transition-all"
                  placeholder={
                    formData.cryptoNetwork === "TRC20"
                      ? "Example: TMfqkbQwMmySXKq4gfzvto2d2xUf3s8L3M"
                      : formData.cryptoNetwork === "ERC20" ||
                          formData.cryptoNetwork === "BEP20" ||
                          formData.cryptoNetwork === "MATIC"
                        ? "Example: 0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb"
                        : formData.cryptoNetwork === "BTC"
                          ? "Example: 1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
                          : formData.cryptoNetwork === "SOL"
                            ? "Example: 7EcDhSYGxXyscszYEp35KHN8vvw3svAuLKTzXwCFLtV"
                            : "Paste your wallet address to receive crypto"
                  }
                  required
                />
                <div className="text-xs text-slate-500">
                  {formData.cryptoNetwork === "TRC20"
                    ? "TRON (TRC20) address starting with T"
                    : formData.cryptoNetwork === "ERC20"
                      ? "Ethereum (ERC20) address starting with 0x"
                      : formData.cryptoNetwork === "BEP20"
                        ? "BNB Smart Chain (BEP20) address starting with 0x"
                        : formData.cryptoNetwork === "MATIC"
                          ? "Polygon (MATIC) address starting with 0x"
                          : formData.cryptoNetwork === "BTC"
                            ? "Bitcoin address starting with 1 or 3"
                            : formData.cryptoNetwork === "SOL"
                              ? "Solana address (base58 format)"
                              : "Paste your wallet address to receive crypto"}
                </div>
                {walletError && (
                  <div
                    role="alert"
                    className="text-xs text-red-400 flex items-center gap-1"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {walletError}
                  </div>
                )}

                {/* Memo/Tag for networks that require it */}
                {formData.cryptoNetwork === "XLM" && (
                  <div className="mt-3">
                    <label className="block text-xs text-slate-400 uppercase mb-2">
                      Wallet Tag / Memo (Required for XLM) *
                    </label>
                    <input
                      type="text"
                      value={formData.walletTag}
                      onChange={(e) =>
                        onFormDataChange({
                          ...formData,
                          walletTag: e.target.value,
                        })
                      }
                      className="w-full bg-slate-900/60 border border-slate-700 rounded-xl px-4 py-3 text-white font-mono text-sm focus:ring-2 focus:ring-purple-500/50 focus:border-transparent outline-none transition-all"
                      placeholder="Enter memo/destination tag"
                      required
                    />
                  </div>
                )}
              </div>
            )}

            {/* Bank Account Section - Only for Offramp */}
            {mode === "OFFRAMP" && (
              <div className="space-y-3">
                <div className="text-sm text-slate-400">
                  <svg
                    className="w-5 h-5 inline mr-2 text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  You'll enter your bank account details on the next step
                </div>
              </div>
            )}

            {/* Exchange Rate Info */}
            {/* <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Exchange Rate</span>
                <span className="text-slate-400">Rate calculated at checkout</span>
              </div>
            </div> */}

            {/* Error Message */}
            {error && (
              <div
                role="alert"
                className="p-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-xl"
              >
                {error}
              </div>
            )}

            {/* Continue Button */}
            <button
              type="submit"
              disabled={isLoading || loadingChannels}
              className="swap-button w-full py-4 rounded-xl text-white font-bold text-lg shadow-lg shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="relative z-10">
                {isLoading
                  ? "Loading..."
                  : loadingChannels
                    ? "Loading currencies..."
                    : "Continue"}
              </span>
            </button>
          </div>
        </form>
      </div>

      {/* Token/Chain Selection Modal */}
      <TokenChainSelectionModal
        isOpen={showTokenModal}
        onClose={() => setShowTokenModal(false)}
        cryptoChannels={cryptoChannels}
        selectedToken={formData.cryptoCurrency}
        selectedChain={formData.cryptoNetwork}
        onSelect={handleTokenChainSelect}
        mode={mode}
      />
    </>
  );
}
